/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ApplicationInsights,
    IEventTelemetry,
    ITraceTelemetry,
    IExceptionTelemetry,
    IPageViewTelemetry,
    IDependencyTelemetry
} from '@microsoft/applicationinsights-web';
import { ITelemetryContext, ITraceLogger, ITraceLoggerParams, LogLevel } from '../interfaces';
import { IDictionary } from '../interfaces/IDictionary';
import { ITrackDependency } from '../interfaces/ITrackDependency';
import { TelemetryBase } from '../telemetry';
import { buildPropertiesList, getPropertiesFromContext, messageTemplateKey, shouldSkipLogging } from './app-insights-helper';
import { safeStringify } from './logging-helper';

/**
 * Client-side logger that sends messages to Application Insights
 */
export class AppInsightsJSLogger implements ITraceLogger {
    /**
     * Mapping between telemetry framework LogLevel and AppInsights JS Severity Levels
     */
    private static _severityLevelMapping: Map<LogLevel, number> = new Map([
        [LogLevel.Trace, 0],
        [LogLevel.Information, 1],
        [LogLevel.Warning, 2],
        [LogLevel.Error, 3],
        [LogLevel.Critical, 4]
    ]);

    private options: ITraceLoggerParams = <ITraceLoggerParams>{};
    // @ts-ignore -- Application Insights instance is located by default at window.appInsights
    private AppInsights: ApplicationInsights = window.appInsights;

    /**
     * Creates a new instance of AppInsights Logger
     * @param instrumentationKey The instrumentation key for the Application Insights instance
     */
    constructor() {
        const eventLog: IEventTelemetry = {
            name: 'Client version of Application Insights registered'
        };
        this.AppInsights.trackEvent(eventLog);
    }

    public initialize(options: ITraceLoggerParams): void {
        this.options = options;
    }

    /**
     * @internal
     * Logs a trace message to AppInsights
     * @param logLevel LogLevel of the message
     * @param context Telemetry context to add to log
     * @param messageTemplate Structured log formatted string
     * @param placeholders List of placeholders in messageTemplate along with position in string
     * @param values Arguments to replace placeholders
     * @param customTags Array of strings to attach to log
     */
    public sendLog(
        logLevel: LogLevel,
        context: ITelemetryContext,
        messageTemplate: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        placeholders: Map<string, any>,
        customTags?: string[],
        exception?: Error
    ): void {
        // Convert ITelemetry LogLevel to corresponding AppInsights SeverityLevel when logging
        const severityLevel = AppInsightsJSLogger._severityLevelMapping.get(logLevel);

        if (shouldSkipLogging(severityLevel, this.options.isDebug)) {
            return;
        }

        // Build properties list with telemetry context and message template arguments
        const properties: IDictionary<string> = buildPropertiesList(context, placeholders, customTags);

        if (exception === undefined) {
            // Send log to AppInsights as trace event
            const traceLog: ITraceTelemetry = {
                message: messageTemplate,
                severityLevel: severityLevel
            };
            this.AppInsights.trackTrace(traceLog, properties);
        } else {
            // Attach message template as custom property
            properties[messageTemplateKey] = messageTemplate;

            const exceptionLog: IExceptionTelemetry = {
                exception: exception,
                properties: properties,
                severityLevel: severityLevel
            };
            // Send log to AppInsights as exception event
            this.AppInsights.trackException(exceptionLog);
        }
    }

    /**
     * @internal
     * Logs a trace message to AppInsights
     * @param logLevel Severity level of the log
     * @param message String to log
     * @param args Any additional objects to log
     * @param context Additional properties to add to the log
     */
    public log(logLevel: LogLevel, message: string, args: unknown[], context: ITelemetryContext): void {
        // Convert ITelemetry LogLevel to corresponding AppInsights SeverityLevel when logging
        const severityLevel = AppInsightsJSLogger._severityLevelMapping.get(logLevel);

        if (shouldSkipLogging(severityLevel, this.options.isDebug)) {
            return;
        }

        // Append any extra arguments to the message
        let fullMessage = message;
        args.forEach(arg => {
            fullMessage = fullMessage.concat('\n', safeStringify(arg));
        });

        const traceLog: ITraceTelemetry = {
            message: `${logLevel}: ${fullMessage}`,
            properties: getPropertiesFromContext(context)
        };
        this.AppInsights.trackTrace(traceLog);
    }
    /**
     * @internal
     * Logs an error object to AppInsights as an exception
     * @param error Error object to log
     * @param context Additional properties to add to the log
     */
    public exception(error: Error, context: ITelemetryContext): void {
        const exceptionLog: IExceptionTelemetry = {
            exception: error,
            properties: getPropertiesFromContext(context)
        };
        this.AppInsights.trackException(exceptionLog);
    }
    /**
     * @internal
     * No-op - not tracking client side metrics
     * @param name name of the metric
     * @param value value of the metric
     */
    public trackMetric(name: string, value: number, context: ITelemetryContext): void {
        // noop
    }
    /**
     * Track a structured event with app insights
     *
     * @template TProperties interface for properties
     * @template TMeasurements interface for measurements to go with the event
     * @param {ITrackEventOptions<TProperties, TMeasurements>} trackEventOptions event options
     */
    public trackEvent<TProperties, TMeasurements>(
        name: string,
        properties?: TProperties,
        measurements?: TMeasurements,
        context?: ITelemetryContext
    ): void {
        const eventLog: IEventTelemetry = {
            name: name,
            properties: <IDictionary<string>>(<unknown>properties),
            measurements: <IDictionary<number>>(<unknown>measurements)
        };
        this.AppInsights.trackEvent(eventLog);
    }

    /**
     * Track a page view
     *
     * @param {string} name
     * @param {string} [url]
     */
    public trackPageView(name: string, url?: string): void {
        const pageViewLog: IPageViewTelemetry = {
            name: name,
            uri: url
        };
        this.AppInsights.trackPageView(pageViewLog);
    }

    /**
     * Track request
     * @param options request options
     */
    public trackDependency(options: ITrackDependency): void {
        const id = TelemetryBase.generateGuid();

        const dependencyLog: IDependencyTelemetry = {
            id: id,
            type: options.method,
            target: `https://${options.target}${options.name}` || '',
            name: options.name || '',
            duration: options.duration,
            success: options.success,
            responseCode: options.resultCode,
            properties: options.properties
        };
        this.AppInsights.trackDependencyData(dependencyLog);
    }
}
